import React, { useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Link, useNavigate } from 'react-router';
import { designerItemsAPI } from '../../api/designerItemsAPI';
import { artistItemsAPI } from '../../api/artistItemsAPI';
import { toast } from 'react-toastify';
import LocationPicker from '../../components/common/LocationPicker';
import { authAPI } from '../../api/authAPI';
import styled from 'styled-components';
import ListingFeeCallout from '../../components/onboarding/ListingFeeCallout';
import ListingItemFlawsField from '../../components/items/ListingItemFlawsField';
import ListingMediaUploadField from '../../components/items/ListingMediaUploadField';
import ListingSizeColorFields from '../../components/items/ListingSizeColorFields';
import PaintingDimensionsFields from '../../components/items/PaintingDimensionsFields';
import ListingCategorySelect from '../../components/items/ListingCategorySelect';
import { LUXURY_ITEM_CATEGORIES } from '../../constants/preOwnedItemCategories';
import {
  EMPTY_CATEGORY_TAXONOMY,
  buildTaxonomySubcategory,
  validateCategoryTaxonomy,
} from '../../constants/listingCategoryTaxonomy';
import {
  getListingSizeColorAttributes,
  buildSizeColorPayload,
} from '../../constants/listingSizeColorAttributes';
import { formatPaintingDimensionsCm, paintingDimensionsError } from '../../utils/paintingDimensions';
import {
  countShopImageFiles,
} from '../../utils/shopMedia';

const PageWrapper = styled.div`
  min-height: 100vh;
  background: linear-gradient(135deg, #a8e6cf 0%, #88d8a3 100%);
  padding: 2rem 1rem 4rem;
`;

const FormCard = styled.div`
  max-width: 560px;
  margin: 0 auto;
  background: rgba(255, 255, 255, 0.95);
  border-radius: 1rem;
  box-shadow: 0 4px 24px rgba(0, 0, 0, 0.08);
  padding: 2rem;
  border: 1px solid rgba(255, 255, 255, 0.8);
`;

const PageTitle = styled.h1`
  font-size: 1.75rem;
  font-weight: 700;
  color: #1e293b;
  margin-bottom: 0.25rem;
  text-align: center;
`;

const PageSubtitle = styled.p`
  font-size: 0.95rem;
  color: #64748b;
  text-align: center;
  margin-bottom: 2rem;
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`;

const Label = styled.label`
  font-weight: 600;
  color: #334155;
  font-size: 0.9rem;
`;

const Input = styled.input`
  padding: 0.75rem 1rem;
  border: 1px solid #e2e8f0;
  border-radius: 0.5rem;
  font-size: 1rem;
  background: #fff;
  color: #1e293b;
  transition: border-color 0.2s, box-shadow 0.2s;
  
  &:focus {
    outline: none;
    border-color: #22c55e;
    box-shadow: 0 0 0 3px rgba(34, 197, 94, 0.15);
  }
  
  &[type="file"] {
    padding: 0.5rem;
    cursor: pointer;
  }
`;

const TextArea = styled.textarea`
  padding: 0.75rem 1rem;
  border: 1px solid #e2e8f0;
  border-radius: 0.5rem;
  font-size: 1rem;
  background: #fff;
  color: #1e293b;
  font-family: inherit;
  min-height: 120px;
  resize: vertical;
  transition: border-color 0.2s;
  
  &:focus {
    outline: none;
    border-color: #22c55e;
  }
`;

const Select = styled.select`
  padding: 0.75rem 1rem;
  border: 1px solid #e2e8f0;
  border-radius: 0.5rem;
  font-size: 1rem;
  background: #fff;
  color: #1e293b;
  cursor: pointer;
  transition: border-color 0.2s;
  
  &:focus {
    outline: none;
    border-color: #22c55e;
  }
`;

const PriceNote = styled.div`
  background: #f0fdf4;
  border: 1px solid #bbf7d0;
  border-radius: 0.5rem;
  padding: 0.75rem 1rem;
  font-size: 0.875rem;
  color: #166534;
  margin-bottom: 0.5rem;
  line-height: 1.5;
`;

const ErrorText = styled.span`
  color: #dc2626;
  font-size: 0.8rem;
`;

const ShopBrandNote = styled.div`
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 0.5rem;
  padding: 0.85rem 1rem;
  font-size: 0.875rem;
  color: #475569;
  line-height: 1.5;

  a {
    color: #166534;
    font-weight: 600;
  }
`;

const PrimaryButton = styled.button`
  padding: 0.875rem 1.5rem;
  background: linear-gradient(135deg, #22c55e 0%, #16a34a 100%);
  color: white;
  border: none;
  border-radius: 0.5rem;
  font-weight: 600;
  font-size: 1rem;
  cursor: pointer;
  transition: all 0.2s;
  margin-top: 0.5rem;
  box-shadow: 0 2px 8px rgba(34, 197, 94, 0.3);
  
  &:hover:not(:disabled) {
    transform: translateY(-1px);
    box-shadow: 0 4px 12px rgba(34, 197, 94, 0.4);
  }
  
  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const SecondaryButton = styled.button`
  padding: 0.75rem 1.5rem;
  background: transparent;
  color: #64748b;
  border: 1px solid #e2e8f0;
  border-radius: 0.5rem;
  font-weight: 500;
  font-size: 1rem;
  cursor: pointer;
  transition: all 0.2s;
  
  &:hover {
    background: #f8fafc;
    border-color: #cbd5e1;
  }
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 1rem;
  margin-top: 1rem;
`;

const OfferDesignerItemPage = ({ vertical = 'designer' }) => {
  const isArtist = vertical === 'artist';
  const catalogApi = isArtist ? artistItemsAPI : designerItemsAPI;
  const browsePath = isArtist ? '/artists' : '/designers';
  const minMedia = isArtist ? 1 : 3;
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { user } = useSelector((state) => state.auth);

  const [shop, setShop] = useState(null);
  const [formData, setFormData] = useState({
    itemName: '',
    description: '',
    price: '',
    color: '',
    paintingLength: '',
    paintingWidth: '',
    ...EMPTY_CATEGORY_TAXONOMY,
  });
  const [selectedSizes, setSelectedSizes] = useState([]);
  const [errors, setErrors] = useState({});
  const listingAttrsRaw = getListingSizeColorAttributes(formData.category);
  const listingAttrs = isArtist && listingAttrsRaw
    ? { ...listingAttrsRaw, requiresSize: false, sizeProfile: null }
    : listingAttrsRaw;
  const [selectedImages, setSelectedImages] = useState([]);
  const mediaUploadApiRef = useRef(null);
  const [itemFlaws, setItemFlaws] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [savingAddress, setSavingAddress] = useState(false);
  const [shippingAddress, setShippingAddress] = useState(user?.shipping_address || '');
  const [addressCoords, setAddressCoords] = useState({
    latitude: user?.latitude ?? null,
    longitude: user?.longitude ?? null,
  });
  const [addressConfirmed, setAddressConfirmed] = useState(Boolean(user?.shipping_address));

  React.useEffect(() => {
    const initialAddress = user?.shipping_address || '';
    setShippingAddress(initialAddress);
    setAddressConfirmed(Boolean(initialAddress));
    setAddressCoords({
      latitude: user?.latitude ?? null,
      longitude: user?.longitude ?? null,
    });
  }, [user]);

  React.useEffect(() => {
    if (!user) return;
    catalogApi.getMyShop()
      .then((data) => setShop(data || null))
      .catch(() => setShop(null));
  }, [user, catalogApi]);

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors[field] || ((field === 'paintingLength' || field === 'paintingWidth') && errors.paintingSize)) {
      setErrors((prev) => ({
        ...prev,
        [field]: '',
        ...(field === 'paintingLength' || field === 'paintingWidth' ? { paintingSize: '' } : {}),
      }));
    }
  };

  const handleTaxonomyChange = (patch) => {
    const categoryChanged = 'category' in patch;
    setFormData((prev) => ({
      ...prev,
      ...patch,
      ...(categoryChanged ? { color: '' } : {}),
    }));
    if (categoryChanged) setSelectedSizes([]);
    setErrors((prev) => {
      const next = { ...prev };
      Object.keys(patch).forEach((key) => { delete next[key]; });
      if (categoryChanged) {
        delete next.color;
        delete next.sizes;
      }
      return next;
    });
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.itemName.trim()) {
      newErrors.itemName = 'Item name is required';
    } else if (formData.itemName.trim().length < 3) {
      newErrors.itemName = 'Item name must be at least 3 characters';
    }

    if (!formData.description.trim()) {
      newErrors.description = 'Description is required';
    } else if (formData.description.trim().length < 25) {
      newErrors.description = `Description must be at least 25 characters (${formData.description.trim().length}/25)`;
    }

    Object.assign(newErrors, validateCategoryTaxonomy(formData));

    if (listingAttrs) {
      if (!formData.color) {
        newErrors.color = 'Color is required';
      }
      if (listingAttrs.requiresSize && selectedSizes.length === 0) {
        newErrors.sizes = 'Select at least one size';
      }
    }

    if (isArtist) {
      const dimError = paintingDimensionsError(formData.paintingLength, formData.paintingWidth);
      if (dimError) newErrors.paintingSize = dimError;
    }

    if (!formData.price.trim()) {
      newErrors.price = 'Price is required';
    } else {
      const price = parseFloat(formData.price);
      if (isNaN(price) || price < 0) {
        newErrors.price = 'Price must be a valid positive number';
      }
    }

    if (!shippingAddress.trim()) {
      newErrors.shipping_address = 'Location is required before listing';
    }
    if (addressCoords.latitude == null || addressCoords.longitude == null) {
      newErrors.shipping_address = 'Please pick your location using the map picker';
    }
    if (!addressConfirmed) {
      newErrors.address_confirm = 'Please confirm your saved location before listing';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const persistListingLocation = async () => {
    setSavingAddress(true);
    try {
      const payload = {
        shipping_address: shippingAddress.trim(),
        latitude: addressCoords.latitude,
        longitude: addressCoords.longitude,
      };
      const response = await authAPI.updateProfile(payload);
      dispatch({ type: 'auth/updateUser', payload: response });
    } finally {
      setSavingAddress(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!user) {
      toast.error(isArtist ? 'Please log in to offer your artist item' : 'Please log in to offer your designer item');
      return;
    }

    if (!validateForm()) {
      toast.error('Please fix the errors in the form');
      return;
    }

    if (selectedImages.length < minMedia) {
      toast.error(`Please upload at least ${minMedia} photo${minMedia === 1 ? '' : 's/videos'}. You have ${selectedImages.length}.`);
      return;
    }
    if (countShopImageFiles(selectedImages) < 1) {
      toast.error('Include at least one photo so the item can show a thumbnail. Videos are optional extras.');
      return;
    }

    try {
      await persistListingLocation();
    } catch (locationError) {
      const message = locationError?.response?.data?.error || 'Could not save location. Please try again.';
      toast.error(typeof message === 'string' ? message : 'Could not save location. Please try again.');
      return;
    }

    setUploading(true);

    try {
      const { uploadCatalogItemResumable } = await import('../../utils/catalogResumableUpload');
      const sizeColor = buildSizeColorPayload(formData.color, selectedSizes);
      const paintingSize = formatPaintingDimensionsCm(formData.paintingLength, formData.paintingWidth);
      const resolvedSubcategory = buildTaxonomySubcategory(formData);

      // Media starts uploading the moment it is picked. Restart anything that
      // never began (or failed) and wait for everything to settle.
      const outcomes = mediaUploadApiRef.current
        ? await mediaUploadApiRef.current.waitForUploads()
        : selectedImages.map(() => null);
      const failedCount = outcomes.filter((o) => !o || (!o.key && !o.deferred)).length;
      if (failedCount > 0) {
        toast.error(
          `${failedCount} photo/video failed to upload. Tap Retry on the failed thumbnail, then submit again.`,
        );
        return;
      }

      await uploadCatalogItemResumable({
        kind: isArtist ? 'artist' : 'designer',
        api: isArtist ? {
          createDraft: catalogApi.createArtistItemDraft,
          uploadMedia: catalogApi.uploadArtistItemMedia,
          finalize: catalogApi.finalizeArtistItemDraft,
          initDirectUpload: catalogApi.initArtistDirectUpload,
          signDirectParts: catalogApi.signArtistDirectParts,
          completeDirectUpload: catalogApi.completeArtistDirectUpload,
          abortDirectUpload: catalogApi.abortArtistDirectUpload,
          attachPending: catalogApi.attachArtistPendingImages,
        } : {
          createDraft: catalogApi.createDesignerItemDraft,
          uploadMedia: catalogApi.uploadDesignerItemMedia,
          finalize: catalogApi.finalizeDesignerItemDraft,
          initDirectUpload: catalogApi.initDesignerDirectUpload,
          signDirectParts: catalogApi.signDesignerDirectParts,
          completeDirectUpload: catalogApi.completeDesignerDirectUpload,
          abortDirectUpload: catalogApi.abortDesignerDirectUpload,
          attachPending: catalogApi.attachDesignerPendingImages,
        },
        fields: {
          ...(shop?.brand_name ? { brand_name: shop.brand_name } : {}),
          item_name: formData.itemName.trim(),
          description: formData.description.trim(),
          price: String(parseFloat(formData.price)),
          category: formData.category,
          subcategory: resolvedSubcategory || undefined,
          ...(isArtist ? {} : { item_flaws: JSON.stringify(itemFlaws) }),
          color: sizeColor.color,
          size: isArtist ? paintingSize : sizeColor.size,
          available_sizes: isArtist ? paintingSize : sizeColor.available_sizes,
        },
        media: selectedImages.map((img, i) => ({
          file: img.file,
          ...(outcomes[i]?.key ? { pendingKey: outcomes[i].key } : {}),
        })),
      });
      toast.success(isArtist
        ? 'Your gallery item was submitted for admin review.'
        : 'Your designer item was submitted for admin review.');
      navigate(browsePath);
    } catch (error) {
      console.error('Error creating catalog item:', error);
      const msg = error.response?.data?.error || error?.message || (typeof error === 'string' ? error : 'Failed to list item');
      toast.error(msg);
    } finally {
      setUploading(false);
    }
  };

  if (!user) {
    return (
      <PageWrapper>
        <FormCard>
          <PageTitle>{isArtist ? 'Offer Item as Artist' : 'Offer Item as Designer'}</PageTitle>
          <p style={{ textAlign: 'center', color: '#64748b' }}>
            {isArtist ? 'Please log in to offer your artist item.' : 'Please log in to offer your designer item.'}
          </p>
        </FormCard>
      </PageWrapper>
    );
  }

  return (
    <PageWrapper>
      <FormCard>
        <PageTitle>{isArtist ? 'Offer Item as Artist' : 'Offer Item as Designer'}</PageTitle>
        <PageSubtitle>
          {isArtist
            ? 'Add a new piece to your gallery. It will appear on Flea after admin approval.'
            : 'Add a new item to your shop. It will appear on Flea after admin approval.'}
        </PageSubtitle>
        <ListingFeeCallout tier="business" />

        <Form onSubmit={handleSubmit}>
          <FormGroup>
            <Label>Listing Location *</Label>
            {shippingAddress.trim() ? (
              <div style={{ padding: '0.75rem', borderRadius: '0.5rem', backgroundColor: '#f8fafc', color: '#1e293b' }}>
                {shippingAddress}
              </div>
            ) : (
              <p style={{ margin: 0, fontSize: '0.875rem', color: '#64748b' }}>
                Pick your address from Google location picker to continue.
              </p>
            )}
            <LocationPicker
              value={shippingAddress}
              onChange={(address, coords) => {
                setShippingAddress(address);
                setAddressConfirmed(false);
                if (coords?.latitude != null && coords?.longitude != null) {
                  setAddressCoords({ latitude: coords.latitude, longitude: coords.longitude });
                }
                if (errors.shipping_address || errors.address_confirm) {
                  setErrors((prev) => ({ ...prev, shipping_address: '', address_confirm: '' }));
                }
              }}
            />
            {shippingAddress.trim() ? (
              <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginTop: '0.5rem' }}>
                <input
                  type="checkbox"
                  checked={addressConfirmed}
                  onChange={(e) => setAddressConfirmed(e.target.checked)}
                />
                Confirm this is my listing location
              </label>
            ) : null}
            {errors.shipping_address && <ErrorText>{errors.shipping_address}</ErrorText>}
            {errors.address_confirm && <ErrorText>{errors.address_confirm}</ErrorText>}
          </FormGroup>

          <ShopBrandNote>
            This item will be listed under <strong>{shop?.brand_name || (isArtist ? 'your gallery' : 'your shop')}</strong>.
            {' '}To change the brand name or logo,{' '}
            {isArtist ? (
              shop ? (
                <Link to={`/artist-shop/${catalogApi.buildShopRef(shop)}`}>open your gallery page</Link>
              ) : (
                'open your gallery page'
              )
            ) : (
              <Link to="/designer-shop/edit">edit your shop</Link>
            )}
            .
          </ShopBrandNote>

          <FormGroup>
            <Label>Item Name *</Label>
            <Input
              type="text"
              value={formData.itemName}
              onChange={(e) => handleInputChange('itemName', e.target.value)}
              placeholder="e.g. Floral Summer Dress, Handcrafted Ring"
              maxLength={150}
            />
            {errors.itemName && <ErrorText>{errors.itemName}</ErrorText>}
          </FormGroup>

          {isArtist ? (
            <PaintingDimensionsFields
              length={formData.paintingLength}
              width={formData.paintingWidth}
              onLengthChange={(v) => handleInputChange('paintingLength', v)}
              onWidthChange={(v) => handleInputChange('paintingWidth', v)}
              error={errors.paintingSize}
            />
          ) : null}

          <ListingCategorySelect
            value={formData}
            onChange={handleTaxonomyChange}
            errors={errors}
            categories={LUXURY_ITEM_CATEGORIES}
            ui={{ FormGroup, Label, Select, ErrorText }}
          />

          <ListingSizeColorFields
            attrs={listingAttrs}
            category={formData.category}
            color={formData.color}
            selectedSizes={selectedSizes}
            onColorChange={(v) => handleInputChange('color', v)}
            onToggleSize={(sizeValue) => {
              setSelectedSizes((prev) =>
                prev.includes(sizeValue) ? prev.filter((s) => s !== sizeValue) : [...prev, sizeValue]
              );
              if (errors.sizes) setErrors((prev) => ({ ...prev, sizes: '' }));
            }}
            errors={errors}
            namePrefix="designer"
          />

          <FormGroup>
            <Label>Description *</Label>
            <TextArea
              value={formData.description}
              onChange={(e) => handleInputChange('description', e.target.value)}
              placeholder="Describe your item in detail: materials, dimensions, care instructions, etc. (minimum 25 characters)"
              rows={5}
            />
            {errors.description && <ErrorText>{errors.description}</ErrorText>}
          </FormGroup>

          {isArtist ? null : (
            <ListingItemFlawsField value={itemFlaws} onChange={setItemFlaws} />
          )}

          <FormGroup>
            <Label>Price (EGP) *</Label>
            <PriceNote>
              Buyers pay the listed price. After the item is accepted, Flea deducts a 5% commission from your payout. Delivery is paid separately by the buyer.
            </PriceNote>
            <Input
              type="number"
              value={formData.price}
              onChange={(e) => handleInputChange('price', e.target.value)}
              placeholder="Enter price in EGP"
              min="0"
              step="1"
            />
            {errors.price && <ErrorText>{errors.price}</ErrorText>}
          </FormGroup>

          <FormGroup>
            <ListingMediaUploadField
              mediaItems={selectedImages}
              onChange={setSelectedImages}
              maxMedia={12}
              minMedia={minMedia}
              minPhotos={1}
              minRecommendedPhotos={isArtist ? 1 : 3}
              idPrefix={isArtist ? 'artist-item' : 'designer-item'}
              label="Photos & videos"
              pickTimeUpload
              uploadApiRef={mediaUploadApiRef}
            />
          </FormGroup>

          <ButtonGroup>
            <PrimaryButton type="submit" disabled={uploading || savingAddress}>
              {uploading || savingAddress ? 'Listing...' : (isArtist ? 'Submit gallery item' : 'List Designer Item')}
            </PrimaryButton>
            <SecondaryButton type="button" onClick={() => navigate(browsePath)}>
              Cancel
            </SecondaryButton>
          </ButtonGroup>
        </Form>
      </FormCard>
    </PageWrapper>
  );
};

export default OfferDesignerItemPage;
